'use client';

import geoContract from '@bells/api/geo/frontend';
import { cn } from '@bells/core/lib/utils';
import { generateRestClient } from '@bells/core/rest/react';
import type { Locale } from '@bells/i18n/runtime';
import {
  RequestLocation,
  RequestLocationType,
} from '@bells/schema/models/mapbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from '@bells/ui/components/command';
import { Command as CommandPrimitive } from 'cmdk';
import { isEmpty, orderBy } from 'lodash';
import { Hotel, House, Loader2, MapPin, Plane } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { crudNotFound } from '@bells/i18n';
import { FlagIcon } from '@bells/ui/components/flag-icon';
const systemRestClient = generateRestClient(geoContract);

export type LocationSearchProps = {
  selected?: {
    name: string;
    countryCode: string;
    lng: number;
    lat: number;
    type: 'address' | 'hotel' | 'country';
  };
};

export const RequestLocationResults = ({
  language,
  onSelect,
  selected,
  term,
  type,
  placeholder,
}: {
  type: RequestLocationType;
  language: Locale;
  onSelect: (val: RequestLocation | undefined) => void;
  term?: string;
  placeholder?: string;
  selected?: string;
}) => {
  const [searchInput, setSearchInput] = useState<string>(term ?? '');

  const [debouncedSearchInput, setDebounceSearchInput] = useDebounce(
    searchInput,
    150
  );

  useEffect(() => {
    setSearchInput(term ?? '');
    setDebounceSearchInput(term ?? '');
  }, [term]);

  const { data, isLoading } = systemRestClient.requestSuggest.useQuery({
    queryKey: ['cities', debouncedSearchInput, type, language],
    queryData: {
      query: { term: debouncedSearchInput, language: language, type },
    },
    enabled:
      debouncedSearchInput && debouncedSearchInput.length > 0 ? true : false,
    select: (df) => df.body,
  });

  const sortedData = useMemo(() => {
    if (!data || data?.length <= 0) {
      return [];
    }

    if (isEmpty(debouncedSearchInput)) {
      const countryPriority = ['de', 'at', 'ct'];
      return orderBy(
        (data ?? []).filter((df) => df && df.name),
        [
          (item) => countryPriority.indexOf(item.country) + 1 || 99,
          (item) => item.name.toLowerCase(),
        ],
        ['asc', 'asc']
      );
    }

    const searchLower = debouncedSearchInput.toLowerCase();
    return orderBy(
      (data ?? []).filter((df) => df && df.name),
      [
        (item) => (item.name.toLowerCase() === searchLower ? 0 : 1),
        (item) => (item.name.toLowerCase().startsWith(searchLower) ? 0 : 1),
        (item) => (item.name.toLowerCase().includes(searchLower) ? 0 : 1),
      ],
      ['asc', 'asc', 'asc']
    );
  }, [data, debouncedSearchInput]);

  return (
    <Command
      shouldFilter={false}
      className='h-full w-full flex-1 bg-transparent'
    >
      <CommandInput
        value={searchInput}
        className='font-medium'
        onValueChange={(val) => setSearchInput(val)}
        placeholder={placeholder}
      />

      <CommandList className='h-full max-h-max bg-transparent'>
        <CommandEmpty>
          {!isLoading && sortedData?.length === 0 && (
            <div className='flex items-center justify-center py-4'>
              {crudNotFound()}
            </div>
          )}
        </CommandEmpty>
        <CommandGroup className={cn('relative h-auto', { hidden: !isLoading })}>
          <div className='relative flex items-center justify-center gap-2 px-2 py-2 uppercase text-muted-foreground'>
            <Loader2 className='h-8 w-8 animate-spin' />
          </div>
        </CommandGroup>
        {sortedData && !isLoading && sortedData?.length > 0 && (
          <CommandGroup className='p-0' heading={undefined}>
            {sortedData.map((item, index) => {
              return (
                <CommandPrimitive.Item
                  value={`${item.name}-${item.lng}-${item.lat}`}
                  onSelect={() => {
                    onSelect(
                      `${item.name}-${item.lng}-${item.lat}` === selected
                        ? undefined
                        : item
                    );
                  }}
                  className={cn(
                    't m-2 flex h-max cursor-pointer select-text items-center gap-2 rounded-lg border p-2 px-3 ring-primary ring-offset-2 transition duration-150 hover:bg-muted data-[active="true"]:border-input data-[active="true"]:ring'
                  )}
                  data-active={
                    `${item.name}-${item.lng}-${item.lat}` === selected
                      ? 'true'
                      : 'false'
                  }
                  key={`suggestion-${index}`}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <div className='flex w-full flex-col items-center gap-1'>
                    <div className='flex w-full justify-between gap-2'>
                      <div className='flex w-full flex-1 items-center gap-2 text-sm font-medium'>
                        <FlagIcon
                          countryCode={item.country.toUpperCase()}
                          className='h-4 w-4'
                        ></FlagIcon>
                        {item.name}
                      </div>

                      {item.type == RequestLocationType.HOTEL && (
                        <Hotel className='h-4 w-4 text-muted-foreground'></Hotel>
                      )}
                      {item.type == RequestLocationType.PRIVATE_ADDRESS && (
                        <House className='h-4 w-4 text-muted-foreground'></House>
                      )}
                      {item.type == RequestLocationType.UNKNOWN && (
                        <MapPin className='h-4 w-4 text-muted-foreground'></MapPin>
                      )}
                      {item.type == RequestLocationType.TRAVEL && (
                        <Plane className='h-4 w-4 text-muted-foreground'></Plane>
                      )}
                    </div>

                    <span className='w-full text-xs opacity-70'>
                      {item.address}
                    </span>
                  </div>
                </CommandPrimitive.Item>
              );
            })}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );
};
